import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="text-purple-600 hover:underline text-sm mb-2 inline-block">
            ← Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="text-gray-600 mt-2">Last updated: November 9, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Book A Day ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you use our platform, services, and applications
              (collectively, the "Services").
            </p>
            <p className="text-gray-700">
              By using our Services, you agree to the collection and use of information in accordance with this Privacy Policy.
              If you do not agree with our policies and practices, please do not use our Services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.1 Information You Provide to Us</h3>
            <p className="text-gray-700 mb-4">We collect information you provide directly to us, including:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Account Information:</strong> Name, email address, password, phone number, and profile information</li>
              <li><strong>Profile Information:</strong> Display name, business name, timezone, professional details, and profile photos</li>
              <li><strong>Payment Information:</strong> Payment card details, billing address, and transaction history (processed by our payment experts)</li>
              <li><strong>Booking Information:</strong> Service bookings, appointments, preferences, and special requests</li>
              <li><strong>Communications:</strong> Messages sent through our platform, customer support inquiries, and feedback</li>
              <li><strong>Identity Verification:</strong> Government-issued ID or professional licenses (for Experts)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.2 Information We Collect Automatically</h3>
            <p className="text-gray-700 mb-4">When you use our Services, we automatically collect:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
              <li><strong>Usage Information:</strong> Pages viewed, features used, search queries, booking patterns</li>
              <li><strong>Location Information:</strong> General location based on IP address or precise location if you grant permission</li>
              <li><strong>Cookies and Tracking:</strong> Session cookies, preference cookies, and analytics data</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.3 Information from Third Parties</h3>
            <p className="text-gray-700">
              We may receive information about you from third-party services, such as social media platforms if you choose to
              connect your account, payment processors, and identity verification services.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Provide, maintain, and improve our Services</li>
              <li>Process bookings and facilitate appointments between Clients and Experts</li>
              <li>Process payments and manage transactions</li>
              <li>Send you confirmations, reminders, and updates about your bookings</li>
              <li>Communicate with you about our Services, including customer support</li>
              <li>Personalize your experience and provide recommendations</li>
              <li>Detect, prevent, and address fraud, security issues, and technical problems</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
              <li>Send you marketing communications (with your consent where required)</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How We Share Your Information</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.1 With Other Users</h3>
            <p className="text-gray-700 mb-4">
              When you book a service, your information (name, contact details, booking details) is shared with the Expert.
              Similarly, Experts' business information is visible to Clients when browsing services.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.2 With Service Experts</h3>
            <p className="text-gray-700 mb-4">
              We share information with third-party service experts who perform services on our behalf, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Payment processors (Stripe, PayPal)</li>
              <li>Cloud hosting experts (AWS, Google Cloud)</li>
              <li>Email and communication services</li>
              <li>Analytics experts</li>
              <li>Customer support tools</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.3 For Legal Purposes</h3>
            <p className="text-gray-700 mb-4">
              We may disclose your information if required to do so by law or in response to valid requests by public
              authorities, such as to comply with a subpoena or similar legal process.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.4 Business Transfers</h3>
            <p className="text-gray-700 mb-4">
              If Book A Day is involved in a merger, acquisition, or sale of assets, your information may be transferred
              as part of that transaction. We will notify you of any such change.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.5 With Your Consent</h3>
            <p className="text-gray-700">
              We may share your information for any other purpose with your consent.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your information for as long as necessary to provide our Services, comply with legal obligations,
              resolve disputes, and enforce our agreements. When you delete your account, we will delete or anonymize your
              information, except where we are required to retain it by law.
            </p>
            <p className="text-gray-700">
              Booking records and transaction data may be retained for accounting, tax, and legal compliance purposes even
              after account deletion.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational measures to protect your information against unauthorized
              access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Employee training on data protection</li>
              <li>Secure data centers and infrastructure</li>
            </ul>
            <p className="text-gray-700">
              However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to
              protect your information, we cannot guarantee its absolute security.
            </p>
          </section>

          {/* Your Rights and Choices */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights and Choices</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.1 Access and Update</h3>
            <p className="text-gray-700 mb-4">
              You can access and update your account information at any time through your account settings.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.2 Data Portability</h3>
            <p className="text-gray-700 mb-4">
              You have the right to request a copy of your personal information in a structured, commonly used format.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.3 Deletion</h3>
            <p className="text-gray-700 mb-4">
              You can request deletion of your account and personal information by contacting us at privacy@bookday.me.
              We will process your request in accordance with applicable laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.4 Marketing Communications</h3>
            <p className="text-gray-700 mb-4">
              You can opt out of marketing emails by clicking the "unsubscribe" link in any marketing email or by updating
              your communication preferences in your account settings.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.5 Cookies</h3>
            <p className="text-gray-700">
              You can control cookies through your browser settings. However, disabling cookies may affect your ability to
              use certain features of our Services.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Data Transfers</h2>
            <p className="text-gray-700">
              Your information may be transferred to and processed in countries other than your country of residence. These
              countries may have data protection laws that differ from those of your country. We ensure that such transfers
              comply with applicable data protection laws and implement appropriate safeguards.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700">
              Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal
              information from children under 18. If you are a parent or guardian and believe your child has provided us
              with personal information, please contact us, and we will delete such information.
            </p>
          </section>

          {/* California Privacy Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. California Privacy Rights</h2>
            <p className="text-gray-700 mb-4">
              If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Right to know what personal information is collected, used, shared, or sold</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
              <li>Right to non-discrimination for exercising your CCPA rights</li>
            </ul>
          </section>

          {/* GDPR Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. European Privacy Rights (GDPR)</h2>
            <p className="text-gray-700 mb-4">
              If you are in the European Economic Area (EEA), you have the following rights under the General Data Protection
              Regulation (GDPR):
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Right of access to your personal data</li>
              <li>Right to rectification of inaccurate personal data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
            <p className="text-gray-700">
              To exercise these rights, please contact us at privacy@bookday.me. You also have the right to lodge a
              complaint with your local data protection authority.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting
              the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this
              Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">
                <strong>Book A Day Privacy Team</strong><br />
                Email: privacy@bookday.me<br />
                Data Protection Officer: dpo@bookday.me<br />
                Address: [Your Business Address]
              </p>
            </div>
          </section>

          {/* Cookie Policy */}
          <section className="border-t border-gray-200 pt-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Cookie Policy</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar tracking technologies to track activity on our Services and store certain information.
              Cookies are files with a small amount of data that are sent to your browser from a website and stored on your device.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Types of Cookies We Use:</h3>
            <ul className="space-y-3 text-gray-700">
              <li>
                <strong>Essential Cookies:</strong> Required for the Services to function properly (e.g., session management, authentication)
              </li>
              <li>
                <strong>Preference Cookies:</strong> Remember your settings and preferences (e.g., language, timezone)
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Help us understand how users interact with our Services (e.g., Google Analytics)
              </li>
              <li>
                <strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and track campaign effectiveness
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
